import { useEffect, useMemo } from 'react';
import * as Tabs from '@givebutter/primitives/tabs';
import { faCircle } from '@fortawesome/pro-regular-svg-icons';
import { faChevronRight, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChecklistCompleteDialog } from 'components/required-checklist/ChecklistCompleteDialog';
import { ChecklistMrButter } from 'components/required-checklist/ChecklistMrButter';
import { RequiredChecklistProgressBar } from 'components/required-checklist/RequiredChecklistProgressBar';
import { RequiredConnectAccount } from 'components/required-checklist/required-items/RequiredConnectAccount';
import { RequiredCreateCampaign } from 'components/required-checklist/required-items/RequiredCreateCampaign';
import { RequiredFirstDollar } from 'components/required-checklist/required-items/RequiredFirstDollar';
import { RequiredVerifyEmail } from 'components/required-checklist/required-items/RequiredVerifyEmail';
import {
    useChecklistData,
    useCompletedAtLeastOneChecklistItem,
    useInvalidateChecklistQuery,
} from 'components/required-checklist/useChecklistData';
import { useRequiredChecklistVisibility } from 'components/required-checklist/useRequiredChecklistVisibility';
import { twMerge } from 'tailwind-merge';

const RequiredHomepageChecklist = () => {
    const { invalidateChecklistQuery } = useInvalidateChecklistQuery();
    const { checklist } = useChecklistData();
    const { verifyEmail, createCampaign, connectPayoutAccount, raiseYourFirstDollar } = checklist;
    const { shouldShowChecklistCompleteDialog } = useRequiredChecklistVisibility();
    const completedAtLeastOne = useCompletedAtLeastOneChecklistItem();

    const defaultTabValue = useMemo(() => {
        const allItems: [string, boolean][] = [
            ['email', verifyEmail],
            ['campaign', createCampaign],
            ['payout', connectPayoutAccount],
            ['dollar', raiseYourFirstDollar],
        ];
        const nextIncompleteItem = allItems.find(value => !value[1])?.[0];
        return nextIncompleteItem ?? 'dollar';
    }, [verifyEmail, createCampaign, connectPayoutAccount, raiseYourFirstDollar]);

    useEffect(() => {
        invalidateChecklistQuery();
    }, [invalidateChecklistQuery]);

    return (
        <div className="w-full rounded-xl shadow-md ring-1 ring-gray-200">
            <div className="flex h-[90px] items-center border-b border-gray-200 px-6">
                <ChecklistMrButter keepGoing={completedAtLeastOne} />
                <div className="flex w-full flex-col">
                    <p className="text-md font-medium">Let's get you set up!</p>
                    <RequiredChecklistProgressBar />
                </div>
            </div>

            <Tabs.Root
                className="flex flex-col sm:flex-row"
                orientation="vertical"
                defaultValue={defaultTabValue}
            >
                <Tabs.List className="flex w-full min-w-72 flex-col overflow-hidden rounded-none border-b border-gray-200 p-0 sm:max-w-96 sm:rounded-bl-xl sm:border-b-0 sm:border-r">
                    <ChecklistTabTrigger isComplete={verifyEmail} value={'email'}>
                        Verify email
                    </ChecklistTabTrigger>
                    <ChecklistTabTrigger isComplete={createCampaign} value={'campaign'}>
                        Create a campaign
                    </ChecklistTabTrigger>
                    <ChecklistTabTrigger isComplete={connectPayoutAccount} value={'payout'}>
                        Connect a payout method
                    </ChecklistTabTrigger>
                    <ChecklistTabTrigger isComplete={raiseYourFirstDollar} value={'dollar'}>
                        Raise your first dollar
                    </ChecklistTabTrigger>
                </Tabs.List>

                <Tabs.Content value={'email'}>
                    <RequiredVerifyEmail />
                </Tabs.Content>
                <Tabs.Content value={'campaign'}>
                    <RequiredCreateCampaign />
                </Tabs.Content>
                <Tabs.Content value={'payout'}>
                    <RequiredConnectAccount />
                </Tabs.Content>
                <Tabs.Content value={'dollar'}>
                    <RequiredFirstDollar />
                </Tabs.Content>
            </Tabs.Root>

            <ChecklistCompleteDialog open={shouldShowChecklistCompleteDialog} />
        </div>
    );
};

interface ChecklistTabTriggerProps extends Omit<Tabs.TabsTriggerProps, 'className'> {
    isComplete: boolean;
}
const ChecklistTabTrigger = ({ children, isComplete, ...rest }: ChecklistTabTriggerProps) => {
    return (
        <Tabs.Trigger
            className="radix-state-active:hover:bg-white group flex h-full min-h-[60px] w-full items-center gap-4 rounded-none border-b border-gray-100 bg-white px-6 py-3.5 text-left last:border-none hover:bg-gray-50"
            {...rest}
        >
            <FontAwesomeIcon
                icon={isComplete ? faCircleCheck : faCircle}
                className={twMerge('size-4', isComplete ? 'text-success-500' : 'text-gray-400')}
            />
            <p
                className={twMerge(
                    'w-full text-sm font-medium',
                    isComplete && '!text-gray-400 line-through',
                    'group-radix-state-active:!text-gray-700 text-gray-500 group-hover:text-gray-700',
                )}
            >
                {children}
            </p>
            <FontAwesomeIcon
                icon={faChevronRight}
                className={
                    'group-radix-state-active:text-gray-600 text-gray-300 group-hover:text-gray-600'
                }
            />
        </Tabs.Trigger>
    );
};

export { RequiredHomepageChecklist };
